import {HttpErrorResponse} from '@angular/common/http';
import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {BehaviorSubject, merge, Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {DistributionAccount} from '../../../../../distribution-account/distribution-account.model';
import {NotificationService} from '../../../../../utils/notification-service';
import {SubscriptionHandler} from '../../../../../utils/subscription-handler';
import {XandrService} from '../../../../../xandr/xandr.service';
import {
  XandrBuyer, XandrDataPriceCategoryBuyerCustomPrice,
  XandrSegmentBilling
} from '../../../../../xandr/xandr.types';

@Component({
  selector: 'app-xandr-buyers',
  templateUrl: './xandr-buyers.component.html',
  styleUrls: ['./xandr-buyers.component.scss']
})
export class XandrBuyersComponent implements OnInit, OnDestroy {
  @ViewChild('autocompleteTrigger') autocompleteTrigger!: MatAutocompleteTrigger;
  @Input() account!: DistributionAccount;
  @Input() billing!: XandrSegmentBilling;
  @Input() buyersName!: string;
  buyerControl = new FormControl();
  loadingAllBuyers = false;
  loadingSharedBuyers = false;
  customPrices: XandrDataPriceCategoryBuyerCustomPrice[] = [];
  allBuyers: XandrBuyer[] = [];
  sharedBuyers: XandrBuyer[] = [];
  filteredBuyers!: Observable<XandrBuyer[]>;
  private dryMod!: BehaviorSubject<string>;
  private subscriptionHandler = new SubscriptionHandler();

  constructor(private xandrService: XandrService,
              private notificationService: NotificationService) {
  }

  ngOnInit(): void {
    this.dryMod = new BehaviorSubject('');
    this.filteredBuyers = merge(this.buyerControl.valueChanges, this.dryMod)
      .pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value.name),
        map(name => name ? this.filterBuyers(name) : this.getAvailableBuyersForSelect())
      );

    this.loadData();
  }

  ngOnDestroy() {
    this.subscriptionHandler.unsubscribe();
  }

  deleteBuyer(buyerToDelete: XandrBuyer) {
    this.loadingSharedBuyers = true;
    this.subscriptionHandler.subscription = this.xandrService.deleteSegmentFromBuyer(
      buyerToDelete.buyerId,
      this.buyersName === 'Publisher' ? +this.account.foreignIdForCurrentSegment : this.billing?.segmentId,
      this.buyersName === 'Publisher',
      this.account,
      this.account.apiId)
      .subscribe({
        next: () => {
          this.loadingSharedBuyers = false;
          this.loadSharedBuyers();
        },
        error: (error: HttpErrorResponse) => {
          this.loadingSharedBuyers = false;
          console.error(error);
        }
      });
  }

  onBuyerSelected(buyer: XandrBuyer, event?: Event) {
    if (!buyer) {
      this.autocompleteTrigger.openPanel();
      event?.stopPropagation();
      return;
    }
    this.buyerControl.setValue('');
    this.loadingSharedBuyers = true;
    this.subscriptionHandler.subscription = this.xandrService.shareSegmentToBuyer(
      buyer.buyerId,
      this.buyersName === 'Publisher' ? +this.account.foreignIdForCurrentSegment : (this.billing?.segmentId || -1),
      this.buyersName === 'Publisher',
      this.account,
      this.account.apiId)
      .subscribe({
        next: () => {
          this.loadingSharedBuyers = false;
          this.loadSharedBuyers();
        },
        error: (error) => {
          this.loadingSharedBuyers = false;
          this.notificationService.error(
            `Error sharing segment ${this.account.foreignIdForCurrentSegment} to ${this.buyersName} ${buyer.buyerId}`);
          console.error(error);
        }
      });
  }

  getAllBuyers() {
    return (this.buyersName === 'Publisher' ?
      this.xandrService.getAllPublishers :
      this.xandrService.getAllBuyers).bind(this.xandrService)(this.account.apiId);
  }

  showLoading(): boolean {
    return this.loadingSharedBuyers || this.loadingAllBuyers;
  }

  forceOpen(event: MouseEvent) {
    this.autocompleteTrigger.openPanel();
    event?.stopPropagation();
  }

  private loadData() {
    this.loadSharedBuyers();
    this.loadAllBuyers();
    this.loadCustomPrices();
  }

  private getSegmentSharing() {
    return (this.buyersName === 'Publisher' ?
      this.xandrService.getPublisherSegmentSharings(this.account.id,this.account.foreignIdForCurrentSegment, this.account.apiId) :
      this.xandrService.getBuyerSegmentSharings(this.account.id,this.account.foreignIdForCurrentSegment, this.account.apiId));
  }

  private loadSharedBuyers() {
    this.loadingSharedBuyers = true;

    this.subscriptionHandler.subscription = this.getSegmentSharing()
      .subscribe({
        next: (data) => {
          this.sharedBuyers = data;
          this.loadingSharedBuyers = false;
          if (this.xandrService.isDryMode) {
            this.dryMod.next('');
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingSharedBuyers = false;
        }
      });
  }

  private loadAllBuyers() {
    this.loadingAllBuyers = true;

    this.subscriptionHandler.subscription = this.getAllBuyers()
      .subscribe({
        next: data => {
          this.loadingAllBuyers = false;
          this.allBuyers = data;
        },
        error: (error) => {
          this.loadingAllBuyers = false;
          console.error(error);
        }
      });
  }

  private loadCustomPrices() {
    this.subscriptionHandler.subscription = this.xandrService.getCustomBuyersPrices(this.account.apiId)
      .subscribe({
        next: data => {
          this.customPrices = data;
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  filteredSharedBuyers(): XandrBuyer[] {
    if (this.buyersName === 'Buyer' && this.billing?.public && !this.xandrService.isDryMode) {
      return this.sharedBuyers.filter(buyer=>this.findCustomPrice(buyer));
    }
    return this.sharedBuyers;
  }

  shouldShowActions(): boolean {
    return this.buyersName === 'Publisher' || (this.buyersName === 'Buyer' && (!this.billing?.public || this.xandrService.isDryMode));
  }

  findCustomPrice(buyer: XandrBuyer): XandrDataPriceCategoryBuyerCustomPrice | undefined {
    return this.customPrices.find(
      customPrice => customPrice.buyerId === buyer.buyerId && customPrice.priceCategoryId === this.billing?.dataCategoryXandr?.id
    );
  }

  private filterBuyers(searchTerm: string): XandrBuyer[] {
    const filterTerm = searchTerm.toLowerCase();
    return this.getAvailableBuyersForSelect().filter(buyer => buyer.name.toLocaleLowerCase().includes(filterTerm)
      || String(buyer.buyerId).includes(searchTerm));
  }

  private getAvailableBuyersForSelect(): XandrBuyer[] {
    if (!this.allBuyers) {
      return [];
    }
    return this.allBuyers.filter(buyer => !this.sharedBuyers.some(shared => shared.buyerId === buyer.buyerId));
  }
}
